/**
 * renderMarkdown.jsx — Inline Discord-style markdown renderer.
 *
 * Issue #46 — renders **bold**, *italic*, _italic_, `code`, and [text](url)
 * hyperlinks inside Fissal chat messages without a full markdown library.
 *
 * Issue #293 — fenced code blocks (```lang … ```) with a per-block "Copy"
 * button. Code content is rendered as a plain text node (no innerHTML),
 * so any HTML/JS payload the model emits is rendered inert by SolidJS.
 *
 * Returns a SolidJS-renderable array; call inside JSX as a child expression:
 *   <div>{renderFissalMarkdown(message.text)}</div>
 */
import { toast } from './toastStore.js';
import { openImageOverlay } from './imageOverlayStore.js';

/**
 * Pattern covering all supported inline tokens (applied in priority order):
 *   1. ![alt](url)  — Markdown images (charts, item icons, screenshots)
 *   2. **bold**
 *   3. *italic* or _italic_
 *   4. `code`
 *   5. [label](url)  — hyperlinks
 *   6. @username     — case-insensitive ASCII tag, e.g. @echotiger
 *   7. gold amount   — "15,000g", "12.5k gold", "3M g", etc.
 *   8. guild name    — canonical Redfur guilds
 *   9. plain comma-grouped number — "1,234" / "12,345,678"
 *   10. standalone URL — bare http(s) links or /charts/ local images
 */
const GUILD_RE  = /\b(Trading Post|Redfur Trading Post|Redfur Dealers|Redfur Caravan|Caravan|Dealers|Post)\b/;
const INLINE_RE = new RegExp([
  /!\[([^\]]*?)\]\((https?:\/\/[^)]+?|\/charts\/[^)]+?|data:image\/[^)]+?)\)/, // 1,2 image (alt, url)
  /\*\*(.+?)\*\*/,                                                              // 3 bold
  /\*(.+?)\*/,                                                                  // 4 italic star
  /_(.+?)_/,                                                                    // 5 italic under
  /`([^`\n]+?)`/,                                                               // 6 code
  /\[([^\]]+?)\]\((https?:\/\/[^)]+?|\/charts\/[^)]+?)\)/,                      // 7,8 link (label, url)
  /(@[A-Za-z0-9_.\-]{2,32})/,                                                   // 9 @username
  /(\d{1,3}(?:,\d{3})+(?:\.\d+)?\s*(?:[kKmMbB])?\s*(?:g|gold|gp)?\b|\d+(?:\.\d+)?\s*[kKmM]\s*(?:g|gold|gp)\b|\d+\s*(?:g|gold|gp)\b)/, // 10 gold
  GUILD_RE,                                                                     // 11 guild name
  /(\d{1,3}(?:,\d{3})+)/,                                                       // 12 plain grouped number
  /(https?:\/\/[^\s<>()]+|\/charts\/[a-zA-Z0-9_\-.]+\.png)/,                    // 13 bare URL or local chart
].map(r => r.source).join('|'), 'g');

// Heuristic: a "gold amount" must contain a digit followed by g/gold/gp,
// OR be a comma-grouped number with a k/M/B suffix. Bare numbers without
// any unit fall through to the plain-number bucket.
function isGoldyValue(s) {
  if (!s) return false;
  return /\d\s*(?:g|gold|gp)\b/.test(s) || /\d\s*[kKmMbB]\b/.test(s);
}

function isImageSource(url) {
  if (!url) return false;
  return url.startsWith('data:image/') || url.startsWith('/charts/') || /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(url);
}

/**
 * Tokenize a single line of text into mixed string/JSX parts.
 * @param {string} line
 * @returns {Array<string|JSX.Element>}
 */
export function tokenizeLine(line) {
  const parts = [];
  let last = 0;
  const re = new RegExp(INLINE_RE.source, INLINE_RE.flags);
  let m;
  while ((m = re.exec(line)) !== null) {
    if (m.index > last) {
      parts.push(line.slice(last, m.index));
    }
    const [
      ,
      imgAlt,
      imgUrl,
      boldText,
      italicStarText,
      italicUnderText,
      codeText,
      linkText,
      linkUrl,
      userText,
      goldText,
      guildText,
      numText,
      bareUrl,
    ] = m;

    if (imgUrl !== undefined) {
      // ![alt](url)
      parts.push(
        <span class="v2-md-img-wrap">
          <img
            src={imgUrl}
            alt={imgAlt || 'Image attachment'}
            class="v2-md-img"
            loading="lazy"
            onClick={() => {
              openImageOverlay(imgUrl, imgAlt, imgAlt || 'Chart / Image');
            }}
          />
          {imgAlt ? <span class="v2-md-img-caption">{imgAlt}</span> : null}
        </span>
      );
    } else if (boldText !== undefined) {
      // **bold**
      parts.push(<strong>{tokenizeLine(boldText)}</strong>);
    } else if (italicStarText !== undefined) {
      // *italic*
      parts.push(<em>{tokenizeLine(italicStarText)}</em>);
    } else if (italicUnderText !== undefined) {
      // _italic_
      parts.push(<em>{tokenizeLine(italicUnderText)}</em>);
    } else if (codeText !== undefined) {
      // `code`
      parts.push(<code class="v2-md-code">{codeText}</code>);
    } else if (linkText !== undefined) {
      // [label](url)
      if (isImageSource(linkUrl)) {
        parts.push(
          <span class="v2-md-img-wrap">
            <img
              src={linkUrl}
              alt={linkText || 'Image'}
              class="v2-md-img"
              loading="lazy"
              onClick={() => {
                openImageOverlay(linkUrl, linkText, linkText || 'Embedded Image');
              }}
            />
            {linkText ? <span class="v2-md-img-caption">{linkText}</span> : null}
          </span>
        );
      } else {
        parts.push(
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            class="v2-md-link"
          >
            {linkText}
          </a>
        );
      }
    } else if (userText !== undefined) {
      // @username
      parts.push(<span class="v2-md-user">{userText}</span>);
    } else if (goldText !== undefined) {
      // Gold amount or comma-grouped value with a k/M suffix.
      parts.push(
        isGoldyValue(goldText)
          ? <span class="v2-md-gold">{goldText}</span>
          : <span class="v2-md-num">{goldText}</span>
      );
    } else if (guildText !== undefined) {
      // Guild name
      parts.push(<span class="v2-md-guild">{guildText}</span>);
    } else if (numText !== undefined) {
      // Plain grouped number
      parts.push(<span class="v2-md-num">{numText}</span>);
    } else if (bareUrl !== undefined) {
      // Bare URL
      if (isImageSource(bareUrl)) {
        parts.push(
          <span class="v2-md-img-wrap">
            <img
              src={bareUrl}
              alt="Embedded chart"
              class="v2-md-img"
              loading="lazy"
              onClick={() => {
                openImageOverlay(bareUrl, 'Embedded Image', 'Embedded Image');
              }}
            />
          </span>
        );
      } else {
        parts.push(
          <a
            href={bareUrl}
            target="_blank"
            rel="noopener noreferrer"
            class="v2-md-link"
          >
            {bareUrl}
          </a>
        );
      }
    }
    last = m.index + m[0].length;
  }
  if (last < line.length) {
    parts.push(line.slice(last));
  }
  return parts;
}

/**
 * Check if a line is a markdown table separator (e.g. |---|---| or |:---:|---:|)
 */
function isTableSeparator(line) {
  if (!line || !line.includes('|')) return false;
  const cells = line.split('|').map(s => s.trim()).filter(Boolean);
  return cells.length > 0 && cells.every(c => /^:?-+:?$/.test(c));
}

/**
 * Split a markdown table row into cells.
 */
function parseTableRow(line) {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map(c => c.trim());
}

/**
 * Render Fissal markdown text as an array of SolidJS nodes.
 *
 * Block-level handling:
 *   - Tables (| col1 | col2 |)       → <div class="v2-md-table-wrap"><table class="v2-md-table">
 *   - `# H`, `## H`, `### H`        → <h3>/<h4>/<h5> with class v2-md-h{N}
 *   - lines starting with `- ` /`* `→ grouped into <ul class="v2-md-ul"><li>
 *   - lines starting with `> `      → <blockquote class="v2-md-bq">
 *   - empty lines                    → thin <div class="v2-md-gap" />
 *   - standalone images / lines      → <div class="v2-md-line">
 *
 * @param {string} text  — pre-cleaned text (run through cleanFissalOutput first)
 * @returns {Array<JSX.Element>|null}
 */
export function renderFissalMarkdown(text) {
  if (!text) return null;
  const out = [];
  const lines = text.split('\n');
  let listBuf = null; // collects consecutive bullet items
  let codeBuf = null; // collects fenced code-block lines
  let codeLang = '';
  let tableBuf = null; // collects consecutive table rows

  function flushList() {
    if (!listBuf) return;
    const items = listBuf;
    listBuf = null;
    out.push(<ul class="v2-md-ul">{items.map((it) => <li>{tokenizeLine(it)}</li>)}</ul>);
  }

  function flushTable() {
    if (!tableBuf) return;
    const rows = tableBuf;
    tableBuf = null;
    if (rows.length === 0) return;

    let headers = null;
    let dataRows = [];

    if (rows.length >= 2 && isTableSeparator(rows[1])) {
      headers = parseTableRow(rows[0]);
      dataRows = rows.slice(2).map(parseTableRow);
    } else {
      dataRows = rows.map(parseTableRow);
    }

    out.push(
      <div class="v2-md-table-wrap">
        <table class="v2-md-table">
          {headers ? (
            <thead>
              <tr>
                {headers.map(h => <th>{tokenizeLine(h)}</th>)}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {dataRows.map(row => (
              <tr>
                {row.map(cell => <td>{tokenizeLine(cell)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  function flushCode() {
    if (codeBuf === null) return;
    const code = codeBuf.join('\n');
    const lang = codeLang;
    codeBuf = null;
    codeLang = '';
    const onCopy = () => {
      try {
        navigator.clipboard.writeText(code).then(
          () => toast.success('Copied code', { ttl: 1200 }),
          () => toast.error('Copy failed'),
        );
      } catch {
        toast.error('Copy failed');
      }
    };
    out.push(
      <pre class="v2-md-pre">
        <div class="v2-md-pre__bar">
          <span class="v2-md-pre__lang">{lang || 'code'}</span>
          <button
            type="button"
            class="v2-md-pre__copy"
            title="Copy code"
            aria-label="Copy code block"
            onClick={onCopy}
          >Copy</button>
        </div>
        <code class={lang ? `v2-md-pre__code language-${lang}` : 'v2-md-pre__code'}>{code}</code>
      </pre>
    );
  }

  for (let idx = 0; idx < lines.length; idx++) {
    const raw = lines[idx];

    // Fenced code block fences (```lang or ```)
    const fence = raw.match(/^\s*```\s*([A-Za-z0-9_+\-]*)\s*$/);
    if (fence) {
      if (codeBuf === null) {
        // Opening fence — flush any pending state.
        flushList();
        flushTable();
        codeBuf = [];
        codeLang = fence[1] || '';
      } else {
        // Closing fence
        flushCode();
      }
      continue;
    }
    if (codeBuf !== null) {
      codeBuf.push(raw);
      continue;
    }

    const line = raw;
    if (!line.trim()) {
      flushList();
      flushTable();
      out.push(<div class="v2-md-gap" />);
      continue;
    }

    // Markdown tables (starts/contains |)
    const isRow = line.trim().startsWith('|') || (line.includes('|') && isTableSeparator(lines[idx + 1]));
    if (isRow) {
      flushList();
      if (!tableBuf) tableBuf = [];
      tableBuf.push(line);
      continue;
    } else {
      flushTable();
    }

    // Headings (#, ##, ###)
    const h = line.match(/^(#{1,3})\s+(.+)$/);
    if (h) {
      flushList();
      const level = h[1].length;
      if (level === 1) {
        out.push(<h3 class="v2-md-h1">{tokenizeLine(h[2])}</h3>);
      } else if (level === 2) {
        out.push(<h4 class="v2-md-h2">{tokenizeLine(h[2])}</h4>);
      } else {
        out.push(<h5 class="v2-md-h3">{tokenizeLine(h[2])}</h5>);
      }
      continue;
    }

    // Bullet lists
    const b = line.match(/^\s*[-*]\s+(.+)$/);
    if (b) {
      if (!listBuf) listBuf = [];
      listBuf.push(b[1]);
      continue;
    }

    // Block quotes
    const q = line.match(/^>\s+(.+)$/);
    if (q) {
      flushList();
      out.push(<blockquote class="v2-md-bq">{tokenizeLine(q[1])}</blockquote>);
      continue;
    }

    flushList();
    out.push(<div class="v2-md-line">{tokenizeLine(line)}</div>);
  }
  flushList();
  flushTable();
  flushCode(); // unclosed fence — render whatever we accumulated
  return out;
}
